import { FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { submitGeneralSupport } from "src/services/FeedbackAPIs";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import StyledTextArea from "src/components/reusable/StyledTextArea";
import StyledInput from "src/components/reusable/StyledInput";
import { FAQ_CATEGORIES, type FaqEntry } from "../Utils/FaqData";
import { getSubmitErrorMessage, pickLocalized } from "../Utils/HelpSupportHelpers";

const getLocalizedFaq = (faq: FaqEntry, isArabic: boolean | undefined) => ({
  question: pickLocalized(faq.question, faq.questionAr, isArabic),
  answer: pickLocalized(faq.answer, faq.answerAr, isArabic),
  bullets: pickLocalized(faq.bullets, faq.bulletsAr, isArabic),
  outro: pickLocalized(faq.outro, faq.outroAr, isArabic),
});

const faqMatches = (faq: FaqEntry, query: string, isArabic: boolean | undefined): boolean => {
  const { question, answer, bullets, outro } = getLocalizedFaq(faq, isArabic);
  return [question, answer, outro, ...(bullets ?? [])].some(
    text => text?.toLowerCase().includes(query)
  );
};

const GeneralSupport: FC = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");
  const SubmitArrowIcon = isArabic ? ArrowLeft : ArrowRight;

  const [search, setSearch] = useState("");
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const [openFaqKey, setOpenFaqKey] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [question, setQuestion] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmedSearch = search.trim().toLowerCase();
  const isSearching = trimmedSearch.length > 0;

  // When searching, keep only categories with matching questions (and only those
  // questions); otherwise show every category with its full set.
  const visibleCategories = useMemo(() => {
    if (!isSearching) {
      return FAQ_CATEGORIES.map(category => ({ category, faqs: category.faqs }));
    }
    return FAQ_CATEGORIES.map(category => ({
      category,
      faqs: category.faqs.filter(faq => faqMatches(faq, trimmedSearch, isArabic)),
    })).filter(group => group.faqs.length > 0);
  }, [isSearching, trimmedSearch, isArabic]);

  const totalMatches = visibleCategories.reduce((sum, group) => sum + group.faqs.length, 0);

  const pendingScrollTarget = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const target = pendingScrollTarget.current;
    if (!target) {return;}
    pendingScrollTarget.current = null;
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  });

  const toggleCategory = (id: string, item: HTMLElement) => {
    const willOpen = openCategoryId !== id;
    setOpenCategoryId(prev => (prev === id ? null : id));
    if (willOpen) {
      pendingScrollTarget.current = item;
    }
  };

  const toggleFaq = (key: string, item: HTMLElement) => {
    const willOpen = openFaqKey !== key;
    setOpenFaqKey(prev => (prev === key ? null : key));
    if (willOpen) {
      pendingScrollTarget.current = item;
    }
  };

  const handleSubmit = async () => {
    if (!question.trim()) {
      message.error(t("helpSupport.generalSupport.validationRequired"), 5);
      return;
    }
    setIsSubmitting(true);
    try {
      await submitGeneralSupport(question.trim());
      message.success(t("helpSupport.generalSupport.submitted"), 5);
      setSubject("");
      setQuestion("");
    } catch (error: unknown) {
      message.error(getSubmitErrorMessage(error, t("helpSupport.generalSupport.submitError")), 5);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="help-support-field">
        <div className="help-support-faq-header">
          <label className="help-support-field__label">
            {t("helpSupport.generalSupport.faqTitle")}
          </label>

          <StyledInput
            className="help-support-search"
            placeholder={t("helpSupport.generalSupport.searchPlaceholder")}
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </div>

        {totalMatches === 0 ? (
          <p className="help-support-field__hint">
            {t("helpSupport.generalSupport.noResults")}
          </p>
        ) : (
          <div className="help-support-faq-categories">
            {visibleCategories.map(({ category, faqs }) => {
              const isCategoryOpen = isSearching || openCategoryId === category.id;
              return (
                <section key={category.id} className="help-support-faq-category">
                  <button
                    type="button"
                    className="help-support-faq-category__header"
                    aria-expanded={isCategoryOpen}
                    onClick={event => {
                      if (!isSearching) {
                        toggleCategory(category.id, event.currentTarget.parentElement as HTMLElement);
                      }
                    }}
                  >
                    <span className="help-support-faq-category__title">
                      <span className="help-support-faq-category__num">{category.number}</span>
                      {pickLocalized(category.title, category.titleAr, isArabic)}
                    </span>
                    <span className="help-support-faq-category__meta">
                      <span className="help-support-faq-category__count">{faqs.length}</span>
                      {!isSearching ? (
                        <ChevronDown
                          size={18}
                          className={`help-support-faq__chevron ${
                            isCategoryOpen ? "help-support-faq__chevron--open" : ""
                          }`}
                          aria-hidden
                        />
                      ) : null}
                    </span>
                  </button>

                  {isCategoryOpen ? (
                    <ul className="help-support-faq help-support-faq--nested">
                      {faqs.map(faq => {
                        const key = `${category.id}::${faq.question}`;
                        const isOpen = openFaqKey === key;
                        const { question, answer, bullets, outro } = getLocalizedFaq(faq, isArabic);
                        return (
                          <li key={key} className="help-support-faq__item">
                            <button
                              type="button"
                              className="help-support-faq__question"
                              aria-expanded={isOpen}
                              onClick={event =>
                                toggleFaq(key, event.currentTarget.parentElement as HTMLElement)
                              }
                            >
                              <span>{question}</span>
                              <ChevronDown
                                size={16}
                                className={`help-support-faq__chevron ${
                                  isOpen ? "help-support-faq__chevron--open" : ""
                                }`}
                                aria-hidden
                              />
                            </button>
                            {isOpen ? (
                              <div className="help-support-faq__answer">
                                <p className="help-support-faq__answer-text">{answer}</p>
                                {bullets ? (
                                  <ul className="help-support-faq__list">
                                    {bullets.map(bullet => (
                                      <li key={bullet}>{bullet}</li>
                                    ))}
                                  </ul>
                                ) : null}
                                {outro ? (
                                  <p className="help-support-faq__answer-text">{outro}</p>
                                ) : null}
                              </div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </section>
              );
            })}
          </div>
        )}
      </div>

      <div className="help-support-field">
        <label className="help-support-field__label">
          {t("helpSupport.generalSupport.askTitle")}
        </label>
        <p className="help-support-field__hint">
          {t("helpSupport.generalSupport.askSubtitle")}
        </p>

        <StyledTextInput
          type="text"
          className="help-support-input"
          placeholder={t("helpSupport.generalSupport.subjectPlaceholder")}
          value={subject}
          onChange={event => setSubject(event.target.value)}
        />

        <StyledTextArea
          className="help-support-textarea"
          rows={4}
          placeholder={t("helpSupport.generalSupport.questionPlaceholder")}
          value={question}
          onChange={event => setQuestion(event.target.value)}
        />
      </div>

      <div className="help-support-submit-row">
        <button
          type="button"
          className="help-support-submit"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {t("helpSupport.generalSupport.submit")}
          <SubmitArrowIcon size={15} aria-hidden />
        </button>
      </div>
    </>
  );
};

export default GeneralSupport;
